import React, { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DateService } from "../../../../utils/dateUtils";
import styles from "./CheckoutSummary.module.css";

interface Group {
  id: number;
  deliveryDate: string | null;
  deliveryMethod: string;
  products: {
    id: number;
    quantity: number;
    price: number;
    product: {
      name: string;
    };
  }[];
}

interface CheckoutSummaryProps {
  billingConfirmed: boolean;
  groups: Group[];
  cartTotals: {
    totalPrice: number;
    totalDeliveryFee: number;
    cartTotal: number;
  };
  isSubmitting: boolean;
  onSubmit: () => Promise<void>;
}

const CheckoutSummary: React.FC<CheckoutSummaryProps> = ({
  billingConfirmed,
  groups,
  cartTotals,
  isSubmitting,
  onSubmit,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => setExpanded(!expanded);

  const formatDate = (date: string | null) =>
    date ? DateService.formatDeliveryDateForDisplay(date) : "Fecha pendiente";

  // Calculate total items across all groups
  const totalItems = groups.reduce(
    (sum, group) => sum + group.products.reduce((s, p) => s + p.quantity, 0),
    0
  );

  return (
    <div className={styles.checkoutSummary}>
      <div className={styles.summaryTitle}>
        <h2>Resumen del Pedido</h2>
      </div>

      <div className={styles.expandButton} onClick={handleExpandClick}>
        <span className={styles.expandLabel}>
          Ver detalles ({totalItems} productos)
        </span>
        <ExpandMoreIcon
          className={`${styles.expandIcon} ${expanded ? styles.expanded : ""}`}
        />
      </div>

      {expanded && (
        <div className={styles.productsContainer}>
          {groups.map((group) => (
            <div key={group.id} className={styles.groupContainer}>
              <div className={styles.groupHeader}>
                <h3 className={styles.groupTitle}>
                  {group.deliveryMethod?.toUpperCase() || "ENTREGA"}
                </h3>
                <span className={styles.deliveryDate}>
                  {formatDate(group.deliveryDate)}
                </span>
              </div>

              <div className={styles.productsList}>
                {group.products.map((product) => (
                  <div key={product.id} className={styles.productItem}>
                    <div className={styles.productDetails}>
                      <span className={styles.productName}>
                        {product.product.name || "Producto sin nombre"}
                      </span>
                      <div className={styles.productPrice}>
                        <span className={styles.productQuantity}>
                          {product.quantity} x
                        </span>
                        <span>S/ {product.price?.toFixed(2) || "0.00"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.summary}>
        <div className={styles.summaryRow}>
          <span>Subtotal</span>
          <span>S/ {cartTotals.totalPrice?.toFixed(2) || "0.00"}</span>
        </div>
        <div className={styles.summaryRow}>
          <span>Costo de Envío</span>
          <span>S/ {cartTotals.totalDeliveryFee?.toFixed(2) || "0.00"}</span>
        </div>
        <div className={`${styles.summaryRow} ${styles.total}`}>
          <strong>Total a pagar</strong>
          <strong>S/ {cartTotals.cartTotal?.toFixed(2) || "0.00"}</strong>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSummary;
